/**
 * AI Settings Service
 *
 * Handles all API calls related to AI configuration and model management.
 */

import apiClient from './api';

export interface ModelInfo {
  name: string;
  display_name: string;
  description: string;
  capabilities: string[];
  is_multimodal: boolean;
  is_recommended: boolean;
}

export interface AvailableModelsResponse {
  provider: string;  // EPIC-23: Include provider in response
  vision_models: ModelInfo[];
  text_models: ModelInfo[];
  embedding_models: ModelInfo[];
  current_vision_model: string;
  current_text_model: string;
  current_embedding_model: string;
}

// EPIC-23: Supported AI providers (type only, list fetched from API)
export type AIProvider = string;

export interface ProviderInfo {
  value: string;
  label: string;
  description: string;
}

export interface AvailableProvidersResponse {
  providers: ProviderInfo[];
  default_provider: string;
}

export interface AIConfig {
  provider: AIProvider;  // EPIC-23: Include provider
  vision_model: string;
  text_model: string;
  temperature: number;
  max_tokens: number;
  project_id: string;
  location: string;
  preferred_language?: string;
  media_resolution?: 'low' | 'medium' | 'high';
  // EPIC-38: Search settings
  vector_similarity_threshold: number;
  tag_search_enabled: boolean;
  embedding_model: string;
}

export interface AIConfigUpdate {
  provider?: AIProvider;  // EPIC-23: Include provider
  vision_model: string;
  text_model: string;
  temperature: number;
  max_tokens: number;
  preferred_language?: string;
  media_resolution?: 'low' | 'medium' | 'high';
  // EPIC-38: Search settings
  vector_similarity_threshold?: number;
  tag_search_enabled?: boolean;
  embedding_model?: string;
}

export interface ModelTestResponse {
  success: boolean;
  model: string;
  message: string;
  response_sample?: string | null;
}

export interface BucketsResponse {
  buckets: string[];
  total: number;
}

export interface SystemConfigResponse {
  gcp_project_id: string;
  gcs_bucket_names: string;
  gcp_location: string;
  database_host: string;
  database_name: string;
  database_status: string;
}

export const aiSettingsService = {
  /**
   * Get list of available AI models for a specific provider
   * EPIC-23: Added provider parameter for multi-provider support
   */
  async getAvailableModels(provider?: AIProvider): Promise<AvailableModelsResponse> {
    const params = provider ? { provider } : {};
    const response = await apiClient.get<AvailableModelsResponse>('/ai-settings/models/available', { params });
    return response.data;
  },

  /**
   * Get list of available AI providers from config
   * EPIC-23: Providers now come from config/ai_models.json instead of static list
   */
  async getAvailableProviders(): Promise<AvailableProvidersResponse> {
    const response = await apiClient.get<AvailableProvidersResponse>('/ai-settings/providers');
    return response.data;
  },

  /**
   * Get current AI configuration
   */
  async getConfig(): Promise<AIConfig> {
    const response = await apiClient.get<AIConfig>('/ai-settings/config');
    return response.data;
  },

  /**
   * Update AI configuration
   */
  async updateConfig(config: AIConfigUpdate): Promise<AIConfig> {
    const response = await apiClient.post<AIConfig>('/ai-settings/config', config);
    return response.data;
  },

  /**
   * Test connection to a specific model
   */
  async testConnection(modelName?: string): Promise<ModelTestResponse> {
    const params = modelName ? { model_name: modelName } : {};
    const response = await apiClient.post<ModelTestResponse>('/ai-settings/test-connection', null, {
      params,
    });
    return response.data;
  },

  /**
   * Get list of accessible GCS buckets
   */
  async getGCSBuckets(): Promise<BucketsResponse> {
    const response = await apiClient.get<BucketsResponse>('/ai-settings/gcs/buckets');
    return response.data;
  },

  /**
   * Get system configuration and status
   */
  async getSystemConfig(): Promise<SystemConfigResponse> {
    const response = await apiClient.get<SystemConfigResponse>('/ai-settings/system');
    return response.data;
  },
};
